import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState, type FC } from 'react';
import type { PermissionGroup } from 'src/apis/authorization-service/types';
import { type GuidedTourRequest } from 'src/apis/authorization-service/use-guided-tour';
import { useGuidedTourUpdate } from 'src/apis/authorization-service/use-guided-tour-update';
import type { OrganisationSettingsRequest } from 'src/apis/authorization-service/use-organisation-settings';
import type { PermissionGroupsRequest } from 'src/apis/authorization-service/use-permission-groups';
import { Card } from 'src/components/data-display/card';
import { EmptyCard } from 'src/components/data-display/empty-card';
import { PageHeader } from 'src/components/layout/page-header';
import { Stack } from 'src/components/layout/stack';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Tour, TourTip, type StepConfig } from 'src/components/overlay/tour';
import { SpinnerContainer } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import { useSaveReferrer } from 'src/hooks/use-referrer';
import { useTitle } from 'src/hooks/use-title';
import { OrganisationSettingsCard } from 'src/pages/settings/permission-groups/overview/organisation-settings-card';
import {
  PermissionGroupCards,
  PermissionGroupCardsWithTour,
} from 'src/pages/settings/permission-groups/overview/permission-group-card';

type Props = {
  permissionGroups: Successful<PermissionGroupsRequest>;
  organisationSettings: Successful<OrganisationSettingsRequest>;
  guidedTour: Successful<GuidedTourRequest>;
  isNewFlowEnabled?: boolean;
};

export type TourTipProps = {
  tourSteps: StepConfig[];
  currentStepIndex: number;
  maxStepIndex: number;
  setCurrentStepIndex: (value: number | ((prev: number) => number)) => void;
  isLast: boolean;
};

interface ExternalLinkProps {
  href: string;
}

export const DocumentationLink: FC<ExternalLinkProps> = ({ href }) => (
  <ExternalLink
    mode="default-underlined"
    target="_blank"
    href={href}
    onClick={(e) => e.stopPropagation()}
  >
    here
  </ExternalLink>
);

const Page: FC<Props> = ({
  permissionGroups,
  organisationSettings,
  guidedTour,
  isNewFlowEnabled = false,
}) => {
  const title = 'Permission Groups';
  useTitle(title);
  const savedReferrer = useSaveReferrer({ label: title });

  const customGroups = permissionGroups.response.data._embedded.items.filter(
    (permissionGroup) => permissionGroup.type === 'CUSTOM'
  );

  const standardGroups = permissionGroups.response.data._embedded.items.filter(
    (permissionGroup) =>
      permissionGroup.type === 'PLATFORM_MANAGED' ||
      permissionGroup.type === 'LEGACY'
  );

  const { groupedStandardGroups, standardSingleGroups } =
    groupStandardPermissionGroups(standardGroups);
  const role = groupedStandardGroups.some((group) =>
    group.groupName.includes('TSO')
  )
    ? 'TSO'
    : 'Shipper';

  const isAdvancedModeEnabled = isNewFlowEnabled
    ? organisationSettings.response.data.isAdvanceModeEnabled || false
    : true;

  const tourSteps: StepConfig[] = [
    {
      title: 'Step 1',
      content:
        'New Permission Group Settings: you can now switch between "Safe" and "Advanced" modes.',
    },
    {
      title: 'Step 2',
      content:
        '"Safe" is our recommended mode and gives you access to all of our Standard Groups.',
    },
    {
      title: 'Step 3',
      content: (
        <>
          "Advanced" is our Beta Mode and allows you to customise your own
          permission groups. Read more about these modes{' '}
          <DocumentationLink href="https://help.prisma-capacity.eu/support/solutions/articles/36000550661" />
          .
        </>
      ),
    },
    {
      title: 'Step 4',
      content: (
        <>
          We recommend assigning the Standard Group to your users, if you wish
          to have a more convenient and streamlined experience. To know more,
          click{' '}
          <DocumentationLink href="https://help.prisma-capacity.eu/support/solutions/articles/36000550665" />
          .
        </>
      ),
    },
    {
      title: 'Step 5',
      content: (
        <>
          You can find out more about Custom Groups{' '}
          <DocumentationLink href="https://help.prisma-capacity.eu/support/solutions/articles/36000506557" />
          .
        </>
      ),
    },
  ] as const;
  const [currentStepIndex, setCurrentStepIndex] = useState(
    guidedTour.response.data.shouldTourBeShown ? 0 : tourSteps.length
  );
  const guidedTourUpdate = useGuidedTourUpdate(guidedTour);
  const targetRef = useRef<HTMLDivElement | null>(null);

  return (
    <Tour steps={tourSteps} storageKey="PERMISSIONS_TOUR">
      {() => (
        <Stack gap={1}>
          <PageHeader
            title={title}
            {...(permissionGroups.response.data._links.create &&
              isAdvancedModeEnabled && {
                actions: (
                  <Link
                    data-testid="create-permission-group-action"
                    mode="button-primary"
                    {...savedReferrer.getLinkProps(
                      '/settings/permission-groups/create'
                    )}
                  >
                    Create Permission Group
                  </Link>
                ),
              })}
            data-testid="permission-groups-heading"
          />

          {permissionGroups.response.data._embedded.items.length ? (
            <SpinnerContainer
              pending={permissionGroups.pending || organisationSettings.pending}
            >
              <Stack gap={2}>
                {isNewFlowEnabled && (
                  <Stack gap={0.75} data-testid="organisation-settings">
                    <Heading mode="section">Settings</Heading>
                    <OrganisationSettingsCard
                      organisationSettingsRequest={organisationSettings}
                      role={role}
                      customGroups={customGroups}
                      permissionGroups={permissionGroups}
                      tourTipProps={{
                        tourSteps,
                        currentStepIndex,
                        maxStepIndex: 3,
                        setCurrentStepIndex,
                        isLast: false,
                      }}
                    ></OrganisationSettingsCard>
                  </Stack>
                )}

                <Stack gap={0.75} data-testid="standard-permission-groups">
                  <Heading mode="section">Standard Groups</Heading>
                  Managed by PRISMA, assignable by admins.
                  <Stack gap={0.5}>
                    {groupedStandardGroups.map((groups) => (
                      <Card
                        key={groups.groupName}
                        data-testid={`${groups.groupName.toLowerCase().replaceAll(' ', '-')}-groups`}
                      >
                        <Stack gap={0.5}>
                          <Stack
                            gap={0.5}
                            flow="column"
                            justifyContent="start"
                            alignItems="baseline"
                          >
                            <Heading mode="card">{groups.groupName}</Heading>

                            <Tooltip
                              content={`We are updating PRISMA's standard permission groups. Soon, we’ll merge the Refactored and Legacy roles into a single role: Standard ${role} User. The Standard ${role} User will be the recommended permission group for most platform users. You may assign your users to both the Refactored and Legacy groups until March 31st. After that, we will automatically transfer them to the Standard User Group`}
                            >
                              {(targetProps) => (
                                <FontAwesomeIcon
                                  {...targetProps}
                                  icon={faInfoCircle}
                                />
                              )}
                            </Tooltip>
                          </Stack>

                          <PermissionGroupCards
                            permissionGroupsRequest={permissionGroups}
                            permissionGroups={groups.items}
                            savedReferrer={savedReferrer}
                            isAdvancedModeEnabled={isAdvancedModeEnabled}
                          />
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                  <PermissionGroupCardsWithTour
                    permissionGroupsRequest={permissionGroups}
                    permissionGroups={standardSingleGroups}
                    savedReferrer={savedReferrer}
                    isAdvancedModeEnabled={isAdvancedModeEnabled}
                    tourTipProps={{
                      tourSteps,
                      currentStepIndex,
                      maxStepIndex: 4,
                      setCurrentStepIndex,
                      isLast: !isAdvancedModeEnabled,
                    }}
                    nextTourTipStep={() => {
                      if (isAdvancedModeEnabled)
                        setCurrentStepIndex((prev) => prev + 1);
                      if (!isAdvancedModeEnabled) {
                        setCurrentStepIndex(tourSteps.length);
                        guidedTourUpdate.execute();
                      }
                      if (targetRef.current) {
                        const elementPosition =
                          targetRef.current.getBoundingClientRect().top +
                          window.scrollY;
                        const offsetPosition = elementPosition - 150; // to avoid menu bar

                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth',
                        });
                      }
                    }}
                  />
                </Stack>
              </Stack>
              {isAdvancedModeEnabled && (
                <TourTip
                  step={{
                    ...tourSteps[currentStepIndex],
                    active: currentStepIndex === 4,
                    isLast: isAdvancedModeEnabled,
                    next: () => {
                      setCurrentStepIndex((prev) => prev + 1);
                      guidedTourUpdate.execute();
                    },
                    exit: () => setCurrentStepIndex(tourSteps.length),
                  }}
                >
                  <Stack gap={0.75} ref={targetRef}>
                    <Stack
                      flow="row"
                      justifyContent="start"
                      gap={0.5}
                      alignItems="baseline"
                    >
                      <Heading mode="section">Custom Groups</Heading>
                      Created and managed by admins.
                    </Stack>

                    {customGroups.length ? (
                      <PermissionGroupCards
                        dataTestId="custom-permission-groups"
                        permissionGroupsRequest={permissionGroups}
                        permissionGroups={customGroups}
                        savedReferrer={savedReferrer}
                        isAdvancedModeEnabled={isAdvancedModeEnabled}
                      />
                    ) : (
                      <EmptyCard
                        label="No custom permission group available."
                        data-testid="custom-permission-groups-empty"
                      />
                    )}
                  </Stack>
                </TourTip>
              )}
            </SpinnerContainer>
          ) : (
            // should never happen
            <EmptyCard
              label="No permission group is available."
              data-testid="permission-groups-empty"
            />
          )}
        </Stack>
      )}
    </Tour>
  );
};

export default Page;

type GroupedStandardGroups = {
  groupedStandardGroups: { groupName: string; items: PermissionGroup[] }[];
  standardSingleGroups: PermissionGroup[];
};

function groupStandardPermissionGroups(
  items: PermissionGroup[]
): GroupedStandardGroups {
  const groupedItems: { [key: string]: PermissionGroup[] } = {};
  const standardSingleGroups: PermissionGroup[] = [];

  items.forEach((item) => {
    if (item.groupedByName) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!groupedItems[item.groupedByName]) {
        groupedItems[item.groupedByName] = [];
      }
      groupedItems[item.groupedByName].push(item);
    } else {
      standardSingleGroups.push(item);
    }
  });

  const groupedStandardGroups = Object.entries(groupedItems).map(
    ([groupName, items]) => ({
      groupName,
      items,
    })
  );

  return { groupedStandardGroups, standardSingleGroups };
}
